import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import Select from 'react-select';
import { debounce } from 'lodash';

export default function AssignmentQuestions() {
  // State management
  const [state, setState] = useState({
    assignments: [],
    questions: [],
    loading: true,
    formSubmitting: false,
    formData: {
      assignmenQuestionId: 0,
      assignmentId: 0,
      assignmentQuestion1: "",
      assignmentQuestionType: "",
      assignmentCorrectAnswer: "",
      assignmentOption1: "",
      assignmentOption2: "",
      assignmentOption3: "",
      assignmentOption4: "",
      questionStatus: true
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchQuestions();
    fetchAssignments();
  }, []);

  // API calls
  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API_LoginURL}assignment/AssignmentMasterList`, {
        headers: getHeaders(),
      });
      updateState({ assignments: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error("Error fetching assignments:", error);
      Swal.fire("Error!", "Failed to load assignments.", "error");
    }
  };

  const fetchQuestions = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(`${API_LoginURL}assignment/AssignmentQuestionList`, {
        headers: getHeaders(),
      });
      updateState({
        questions: Array.isArray(response.data) ? response.data : [],
        loading: false
      });
    } catch (error) {
      console.error("Error fetching questions:", error);
      Swal.fire("Error!", "Failed to load questions.", "error");
      updateState({ loading: false });
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: value
      }
    });
  };

  const handleSelectChange = (selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        assignmentId: selectedOption ? selectedOption.value : 0
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        assignmenQuestionId: 0,
        assignmentId: 0,
        assignmentQuestion1: "",
        assignmentQuestionType: "",
        assignmentCorrectAnswer: "",
        assignmentOption1: "",
        assignmentOption2: "",
        assignmentOption3: "",
        assignmentOption4: "",
        questionStatus: true
      }
    });
  };

  const handleEdit = (question) => {
    updateState({ 
      formData: {
        ...question,
        questionStatus: question.questionStatus !== false
      }
    });
  };

  const handleDelete = async (assignmenQuestionId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}assignment/DeleteAssignmentQuestion`,
        { assignmenQuestionId },
        { headers: getHeaders() }
      );
      updateState({
        questions: state.questions.filter(item => item.assignmenQuestionId !== assignmenQuestionId)
      });
      Swal.fire("Deleted!", "Question has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting question:", error);
      Swal.fire("Error", "Failed to delete question", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    // Validate required fields
    if (!state.formData.assignmentId || !state.formData.assignmentQuestion1 || !state.formData.assignmentQuestionType) {
      Swal.fire("Error", "Please fill all required fields", "error");
      return;
    }

    // If question type is multichoice, validate options
    if (state.formData.assignmentQuestionType === "Choice" && 
        (!state.formData.assignmentOption1 || !state.formData.assignmentOption2)) {
      Swal.fire("Error", "Please provide at least two options for multiple choice questions", "error");
      return;
    }

    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}assignment/AssignmentQuestionAddEdit`,
        state.formData,
        { headers: getHeaders() }
      );

      Swal.fire({
        icon: response.data.response === "SUCCESS" ? "success" : "error",
        title: response.data.response === "SUCCESS" ? "Success" : "Error",
        text: response.data.response === "SUCCESS" 
          ? "Question saved successfully" 
          : response.data.response,
      });

      if (response.data.response === "SUCCESS") {
        fetchQuestions();
        document.getElementById("closeQuestionModal").click();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving question:", error);
      Swal.fire("Error", "Failed to save question", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  // Filter and pagination
  const filteredQuestions = state.questions.filter(item => {
    const matchesSearch = 
      item.assignmentQuestion1?.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      (item.assignmentTittle && item.assignmentTittle?.toLowerCase().includes(state.searchQuery.toLowerCase()));
    
    const matchesStatus = 
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.questionStatus) ||
      (state.filterStatus === "inactive" && !item.questionStatus);
    
    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredQuestions.length / state.itemsPerPage);
  const displayedQuestions = filteredQuestions.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  // Get assignment title for display
  const getAssignmentTitle = (assignmentId) => {
    const assignment = state.assignments.find(a => a.assignmentId === assignmentId);
    return assignment ? assignment.assignmentTittle : "N/A";
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">Assignment Questions</h4>
                    
                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Question or Assignment"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>
                      
                      {/* Filter */}
                      {/* <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div> */}
                      
                      {/* Add Question Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#questionModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Question</span>
                      </button>
                    </div>
                  </div>

                  {/* Questions Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <ClipLoader color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading questions...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="10%">ID</th>
                              <th width="25%">Assignment</th>
                              <th width="30%">Question</th>
                              <th width="15%">Type</th>
                              {/* <th width="10%">Status</th> */}
                              <th width="10%" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedQuestions.length > 0 ? (
                              displayedQuestions.map((item) => (
                                <tr key={item.assignmenQuestionId}>
                                  <td>{item.assignmenQuestionId}</td>
                                  <td>{getAssignmentTitle(item.assignmentId)}</td>
                                  <td>{item.assignmentQuestion1}</td>
                                  <td>{item.assignmentQuestionType}</td>
                                  {/* <td
                                    style={{
                                      color: item.questionStatus
                                        ? "red"
                                        : "green",
                                    }}
                                  >
                                    {item.questionStatus ?  "Inactive" : "Active"}
                                  </td> */}
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#questionModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.assignmenQuestionId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="6" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No questions"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No questions found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#questionModal"
                                      onClick={resetForm}
                                    >
                                      Add New Question
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedQuestions.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Modal */}
      <div className="modal fade" id="questionModal" tabIndex="-1" aria-labelledby="questionModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="questionModalLabel">
                {state.formData.assignmenQuestionId > 0 ? 'Edit Question' : 'Add New Question'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row mb-3">
                <div className="col-md-6 pe-0">
  <label htmlFor="assignmentId" className="form-label required">Assignment</label>
  <Select
    id="assignmentId"
    options={state.assignments.map(assignment => ({
      value: assignment.assignmentId,
      label: assignment.assignmentTittle // Note: Check if this should be 'assignmentTitle' (spelling)
    }))}
    value={state.assignments.find(option => option.assignmentId === state.formData.assignmentId) ? {
      value: state.formData.assignmentId,
      label: state.assignments.find(option => option.assignmentId === state.formData.assignmentId).assignmentTittle
    } : null}
    onChange={handleSelectChange}
    placeholder="Select Assignment"
    className="basic-single"
    classNamePrefix="select"
    isClearable
    isSearchable
  />
</div>
                  <div className="col-md-5 ps-0 flex-grow-1">
                    <label htmlFor="assignmentQuestionType" className="form-label required">Question Type</label>
                    <select
                      className="form-select"
                      id="assignmentQuestionType"
                      name="assignmentQuestionType"
                      value={state.formData.assignmentQuestionType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Choice">Multiple Choice</option>
                      <option value="Short Answer">Short Answer</option>
                      <option value="Long Answer">Long Answer</option>
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                <div className="col-md-12 mb-2">
                  <label htmlFor="assignmentQuestion1" className="form-label required">Question</label>
                  <textarea
                    className="form-control"
                    id="assignmentQuestion1"
                    name="assignmentQuestion1"
                    value={state.formData.assignmentQuestion1}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                </div>

                {state.formData.assignmentQuestionType === "Choice" && (
                  <div className="mb-3">
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <label htmlFor="assignmentOption1" className="form-label required">Option 1</label>
                        <input
                          type="text"
                          className="form-control"
                          id="assignmentOption1"
                          name="assignmentOption1"
                          value={state.formData.assignmentOption1}
                          onChange={handleInputChange}
                          required={state.formData.assignmentQuestionType === "Choice"}
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label htmlFor="assignmentOption2" className="form-label required">Option 2</label>
                        <input
                          type="text"
                          className="form-control"
                          id="assignmentOption2"
                          name="assignmentOption2"
                          value={state.formData.assignmentOption2}
                          onChange={handleInputChange}
                          required={state.formData.assignmentQuestionType === "Choice"}
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label htmlFor="assignmentOption3" className="form-label">Option 3</label>
                        <input
                          type="text"
                          className="form-control"
                          id="assignmentOption3"
                          name="assignmentOption3"
                          value={state.formData.assignmentOption3}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-md-6 mb-2">
                        <label htmlFor="assignmentOption4" className="form-label">Option 4</label>
                        <input
                          type="text"
                          className="form-control"
                          id="assignmentOption4"
                          name="assignmentOption4"
                          value={state.formData.assignmentOption4}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
 <div className="row mb-3">
                <div className="col-md-12 mb-2">
                  <label htmlFor="assignmentCorrectAnswer" className="form-label">
                    Correct Answer {state.formData.assignmentQuestionType === "Choice" && "(Enter option)"}
                  </label>
                  {state.formData.assignmentQuestionType === "Choice" ? (
                    <input
                      type="text"
                      className="form-control"
                      id="assignmentCorrectAnswer"
                      name="assignmentCorrectAnswer"
                      value={state.formData.assignmentCorrectAnswer}
                      onChange={handleInputChange}
                      placeholder=""
                    />
                  ) : (
                    <textarea
                      className="form-control"
                      id="assignmentCorrectAnswer"
                      name="assignmentCorrectAnswer"
                      value={state.formData.assignmentCorrectAnswer}
                      onChange={handleInputChange}
                    />
                  )}
                </div>
                </div>
                {/* {state.formData.assignmenQuestionId > 0 && (
                  <div className="mb-3">
                    <label htmlFor="questionStatus" className="form-label">Status</label>
                    <select
                      className="form-select"
                      id="questionStatus"
                      name="questionStatus"
                      value={state.formData.questionStatus}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                )} */}
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" id="closeQuestionModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}